import React, { useState, useEffect, useRef, useContext } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";

type DateRangePickerProps = {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  onChange: (start: string, end: string) => void;
  className?: string;
  mode?: "past" | "future" | "both";
};

type Preset = "All" | "Today" | "Yesterday" | "Last 7 days" | "Last 30 days" | "Next 7 days" | "Next 30 days" | "Custom";

const PAST_PRESETS: { label: Preset; getValue: () => [string, string] }[] = [
  { label: "All", getValue: () => ["", ""] },
  {
    label: "Today",
    getValue: () => [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
  },
  {
    label: "Yesterday",
    getValue: () => [
      moment().subtract(1, "days").format("YYYY-MM-DD"),
      moment().subtract(1, "days").format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Last 7 days",
    getValue: () => [
      moment().subtract(6, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Last 30 days",
    getValue: () => [
      moment().subtract(29, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ],
  },
];

const FUTURE_PRESETS: { label: Preset; getValue: () => [string, string] }[] = [
  { label: "All", getValue: () => ["", ""] },
  {
    label: "Today",
    getValue: () => [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
  },
  {
    label: "Next 7 days",
    getValue: () => [
      moment().format("YYYY-MM-DD"),
      moment().add(6, "days").format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Next 30 days",
    getValue: () => [
      moment().format("YYYY-MM-DD"),
      moment().add(29, "days").format("YYYY-MM-DD"),
    ],
  },
];

const BOTH_PRESETS: { label: Preset; getValue: () => [string, string] }[] = [
  { label: "All", getValue: () => ["", ""] },
  {
    label: "Today",
    getValue: () => [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
  },
  {
    label: "Yesterday",
    getValue: () => [
      moment().subtract(1, "days").format("YYYY-MM-DD"),
      moment().subtract(1, "days").format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Last 7 days",
    getValue: () => [
      moment().subtract(6, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Last 30 days",
    getValue: () => [
      moment().subtract(29, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Next 7 days",
    getValue: () => [
      moment().format("YYYY-MM-DD"),
      moment().add(6, "days").format("YYYY-MM-DD"),
    ],
  },
  {
    label: "Next 30 days",
    getValue: () => [
      moment().format("YYYY-MM-DD"),
      moment().add(29, "days").format("YYYY-MM-DD"),
    ],
  },
];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  className,
  mode = "past",
}) => {
  const { theme } = useContext(CMSModal);
  const PRESETS = mode === "future" ? FUTURE_PRESETS : mode === "both" ? BOTH_PRESETS : PAST_PRESETS;
  const [activePreset, setActivePreset] = useState<Preset>("Custom");
  // Manage internal view date (center around selection or current date)
  const [viewDate, setViewDate] = useState(moment(endDate || undefined));

  // Determine active preset on mount/update
  useEffect(() => {
    const match = PRESETS.find((p) => {
      if (p.label === "Custom" || p.label === "All") return false;
      const [s, e] = p.getValue();
      return s === startDate && e === endDate;
    });
    if (match) setActivePreset(match.label);
    else if (!startDate && !endDate) setActivePreset("All");
    else setActivePreset("Custom");
  }, [startDate, endDate]);

  const handlePresetClick = (preset: typeof PRESETS[0]) => {
    setActivePreset(preset.label);
    const [s, e] = preset.getValue();
    if (preset.label === "All") {
      onChange("", ""); // Or however you want to signify 'All'
    } else {
      onChange(s, e);
      setViewDate(moment(e));
    }
    // Close popover logic could go here if we had access to close()
  };

  const handleDateClick = (dateStr: string, close: () => void) => {
    setActivePreset("Custom");
  };

  // Internal selection state to handle the "click start then click end" flow
  const [internalStart, setInternalStart] = useState<string | null>(null);

  const onCalendarDayClick = (dayStr: string, closeCallback: () => void) => {
    setActivePreset("Custom");

    if (!internalStart) {
      setInternalStart(dayStr);
      onChange(dayStr, dayStr); // Show as single day selected
    } else {
      // We have a start, is this day before or after?
      if (moment(dayStr).isBefore(internalStart)) {
        // User clicked a date before the current start, treat as new start
        setInternalStart(dayStr);
        onChange(dayStr, dayStr);
      } else {
        // User clicked after, complete the range
        onChange(internalStart, dayStr);
        setInternalStart(null); // Reset selection flow
        // Close the popover since custom range is done
        closeCallback();
      }
    }
  };


  const renderCalendar = (baseDate: moment.Moment, closeCallback: () => void) => {
    const startOfMonth = baseDate.clone().startOf("month");
    const endOfMonth = baseDate.clone().endOf("month");
    const startDay = startOfMonth.day(); // 0 is Sunday
    const daysInMonth = baseDate.daysInMonth();

    const days = [];
    // Padding
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`pad-${i}`} className="w-8 h-8 lg:w-10 lg:h-10" />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const current = baseDate.clone().date(d);
      const str = current.format("YYYY-MM-DD");

      const isSelectedStart = str === startDate;
      const isSelectedEnd = str === endDate;
      const isInRange = startDate && endDate && current.isBetween(startDate, endDate, 'day', '[]');
      const isToday = str === moment().format("YYYY-MM-DD");

      days.push(
        <button
          key={str}
          onClick={() => onCalendarDayClick(str, closeCallback)}
          className={twMerge(
            "w-8 h-8 lg:w-10 lg:h-10 text-sm flex items-center justify-center rounded-full transition-colors relative z-10",
            isInRange
              ? (theme === "dark" ? "bg-[#1a3a4a] text-blue-300" : "bg-[#f0f9ff] text-[#004175]")
              : (theme === "dark" ? "text-gray-300 hover:bg-gray-800" : " hover:bg-gray-100"),
            isSelectedStart ? "bg-[#099ed1] text-white rounded-full !rounded-full" : "",
            isSelectedEnd ? "bg-[#099ed1] text-white rounded-full !rounded-full" : "",
            isSelectedStart && isSelectedEnd ? "!rounded-full" : "",
            isToday && !isInRange ? "text-[#099ed1] font-bold" : ""
          )}
        >
          {d}
        </button>
      );
    }

    return (
      <div className="p-4 w-full max-w-[320px]">
        <div className={twMerge("flex items-center justify-center mb-4 font-semibold transition-colors", theme === "dark" ? "text-gray-100" : "")}>
          {baseDate.format("YYYY MMMM")}
        </div>
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-xs text-gray-400 font-medium">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {days}
        </div>
      </div>
    );
  };

  const handlePrevMonth = () => setViewDate(d => d.clone().subtract(1, 'month'));
  const handleNextMonth = () => setViewDate(d => d.clone().add(1, 'month'));

  const displayString = startDate && endDate
    ? `${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format("MMM D, YYYY")}`
    : activePreset === "All" ? "All Time" : "Select Date Range";

  const getPresetLabel = () => {
    if (activePreset && activePreset !== "Custom") return activePreset;
    return "Custom";
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button className={twMerge(
            "flex items-center justify-between gap-2 border rounded-md text-sm transition-colors min-w-[200px] lg:min-w-[348px] w-full lg:w-auto h-[40px]",
            theme === "dark" ? "bg-[#262727] border-gray-700 text-gray-300 hover:border-gray-500" : "bg-white border-gray-300  hover:border-[#099ed1]",
            className
          )}>
            <div className="flex items-center h-full w-full">
              {/* Left side: Preset Label (Desktop only) */}
              <div className={twMerge(
                "hidden lg:flex items-center px-4 border-r h-full font-medium rounded-l-md transition-colors text-[#1bbae1] font-medium",
                theme === "dark" ? "bg-[#1f1f1f] border-gray-700 " : "bg-gray-50 border-gray-200"
              )}>
                {getPresetLabel()}
              </div>

              {/* Right side: Date Range */}
              <div className="flex items-center gap-2 px-3 flex-1">
                <CalendarIcon size={16} className="text-gray-500" />
                <span>{displayString}</span>
              </div>
            </div>

            {/* Chevron for select-like feel */}
            <div className="pr-3">
              <ChevronRight size={16} className="text-gray-400 rotate-90" />
            </div>
          </Popover.Button>

          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className={twMerge(
              "absolute  lg:-left-[50%] top-full mt-2 rounded-lg shadow-xl border z-50 flex flex-col lg:flex-row overflow-hidden w-full lg:w-[850px] transition-colors",
              theme === "dark" ? "bg-[#262727] border-gray-700" : "bg-white border-gray-200"
            )}>

              {/* Sidebar */}
              <div className={twMerge(
                "flex flex-col border-b lg:border-b-0 lg:border-r w-full lg:w-40 transition-colors",
                theme === "dark" ? "border-gray-700 bg-[#1f1f1f]" : "border-gray-200 bg-gray-50"
              )}>

                <div className="flex flex-col overflow-auto p-2 gap-1">
                  {PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => {
                        handlePresetClick(p);
                        // Auto close for all presets except 'Custom'? 
                        // User said "Close dropdown after selecting", probably implies presets.
                        // Custom might need to stay open for calendar interaction.
                        if (p.label !== "Custom") {
                          close();
                        }
                      }}
                      className={twMerge(
                        "px-3 py-2 text-left text-sm rounded-md transition-all whitespace-nowrap flex-shrink-0 relative",
                        activePreset === p.label
                          ? (theme === "dark" ? "bg-gray-800 text-[#099ed1] font-bold shadow-sm border-l-4 border-[#099ed1]" : "bg-white text-[#099ed1] font-bold shadow-sm border-l-4 border-[#099ed1]")
                          : (theme === "dark" ? "text-gray-400 hover:bg-gray-800 border-l-4 border-transparent" : "text-gray-600 hover:bg-gray-100 border-l-4 border-transparent")
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={twMerge("hidden lg:flex flex-col relative p-2 transition-colors", theme === "dark" ? "bg-[#262727]" : "")}>

                <div className={twMerge("px-4 py-2 border-b mb-2 flex justify-between items-center transition-colors", theme === "dark" ? "border-white/5" : "border-gray-100")}>
                  <span className={twMerge("text-sm font-semibold transition-colors", theme === "dark" ? "text-gray-200" : "")}>
                    {getPresetLabel()}: <span className={twMerge("font-normal transition-colors", theme === "dark" ? "text-gray-500" : "text-gray-500")}>{startDate && endDate ? `${moment(startDate).format("MMM D")} - ${moment(endDate).format("MMM D")}` : "Select Range"}</span>
                  </span>
                </div>

                <div className="absolute top-14 left-4 z-20">
                  <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
                    <ChevronLeft size={20} />
                  </button>
                </div>
                <div className="absolute top-14 right-4 z-20">
                  <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
                    <ChevronRight size={20} />
                  </button>
                </div>

                <div className={twMerge("flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x transition-colors", theme === "dark" ? "divide-white/5" : "divide-gray-100")}>
                  {renderCalendar(viewDate.clone().subtract(1, 'month'), close)}
                  {renderCalendar(viewDate.clone(), close)}
                </div>
              </div>

            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default DateRangePicker;
